import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MdOutlineExpandMore } from "react-icons/md"; // dropdown indicator
import './Dropdown.css';

const Dropdown = ({
    options = [],
    selectedOption,
    onSelect,
    searchable = false,
    searchPlaceholder = 'Search...',
    optionIconKey = 'icon',
    renderOptionIcon,
    getSelectedLabel,
    menuPortal = false,
    menuPortalMinWidth = 0,
    menuPortalMaxHeight = 220,
    menuPortalClassName = '',
    menuPortalZIndex = 3100,
    showSelectedOnlyUntilSearch = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [menuPosition, setMenuPosition] = useState(null);
    const dropdownRef = useRef(null);
    const menuRef = useRef(null);
    const triggerRef = useRef(null);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const handleOptionClick = (option) => {
        onSelect(option);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleClickOutside = (event) => {
        const inRoot = dropdownRef.current?.contains(event.target);
        const inMenu = menuRef.current?.contains(event.target);
        if (!inRoot && !inMenu) {
            setIsOpen(false);
        }
    };

    const scrollMenuBy = useCallback((delta) => {
        const menu = menuRef.current;
        if (!menu) return;
        menu.scrollTop = Math.max(0, Math.min(menu.scrollHeight - menu.clientHeight, menu.scrollTop + delta));
    }, []);

    const handleMenuKeyDown = useCallback((event) => {
        const menu = menuRef.current;
        if (!menu) return;
        const step = 36;
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                scrollMenuBy(step);
                break;
            case 'ArrowUp':
                event.preventDefault();
                scrollMenuBy(-step);
                break;
            case 'PageDown':
                event.preventDefault();
                scrollMenuBy(menu.clientHeight);
                break;
            case 'PageUp':
                event.preventDefault();
                scrollMenuBy(-menu.clientHeight);
                break;
            case 'Home':
                event.preventDefault();
                menu.scrollTop = 0;
                break;
            case 'End':
                event.preventDefault();
                menu.scrollTop = menu.scrollHeight;
                break;
            default:
                break;
        }
    }, [scrollMenuBy]);

    const handleSearchKeyDown = useCallback(
        (event) => {
            if (event.key === 'ArrowDown' || event.key === 'PageDown') {
                event.preventDefault();
                const menu = menuRef.current;
                if (!menu) return;
                scrollMenuBy(event.key === 'PageDown' ? menu.clientHeight : 36);
            } else if (event.key === 'ArrowUp' || event.key === 'PageUp') {
                event.preventDefault();
                const menu = menuRef.current;
                if (!menu) return;
                scrollMenuBy(event.key === 'PageUp' ? -menu.clientHeight : -36);
            }
        },
        [scrollMenuBy]
    );

    const updateMenuPosition = useCallback(() => {
        if (!menuPortal || !triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        const heightCap = menuPortalMaxHeight === false ? null : menuPortalMaxHeight ?? 220;
        const menuMaxH = heightCap ?? Number.MAX_SAFE_INTEGER;
        const gap = 4;
        const spaceBelow = window.innerHeight - rect.bottom - gap;
        const spaceAbove = rect.top - gap;
        const openUp = heightCap != null && spaceBelow < Math.min(menuMaxH, 160) && spaceAbove > spaceBelow;
        const maxHeight =
            heightCap == null ? undefined : Math.max(120, Math.min(menuMaxH, openUp ? spaceAbove : spaceBelow));

        const viewportPad = 8;
        const maxMenuWidth = Math.max(120, window.innerWidth - viewportPad * 2);
        const minWidth = Math.max(0, Number(menuPortalMinWidth) || 0);
        const width = Math.min(maxMenuWidth, Math.max(rect.width, minWidth));
        let left = rect.left;
        if (width > rect.width) {
            left = rect.right - width;
        }
        left = Math.max(viewportPad, Math.min(left, window.innerWidth - width - viewportPad));

        setMenuPosition({
            position: 'fixed',
            left,
            width,
            zIndex: menuPortalZIndex,
            ...(maxHeight != null ? { maxHeight } : {}),
            ...(openUp
                ? { bottom: window.innerHeight - rect.top + gap }
                : { top: rect.bottom + gap }),
        });
    }, [menuPortal, menuPortalMinWidth, menuPortalMaxHeight, menuPortalZIndex]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside, { passive: true });
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isOpen]);

    const filteredOptions = useMemo(() => {
        const q = searchTerm.trim();
        if (searchable && !q && showSelectedOnlyUntilSearch && selectedOption) {
            const narrowed = options.filter((option) => option.value === selectedOption);
            if (narrowed.length) return narrowed;
        }
        if (!searchable || !q) return options;
        const lower = q.toLowerCase();
        return options.filter((option) => String(option.label || '').toLowerCase().includes(lower));
    }, [options, searchable, searchTerm, showSelectedOnlyUntilSearch, selectedOption]);

    useLayoutEffect(() => {
        if (!isOpen || !menuPortal) {
            setMenuPosition(null);
            return undefined;
        }
        updateMenuPosition();
        window.addEventListener('resize', updateMenuPosition);
        window.addEventListener('scroll', updateMenuPosition, true);
        return () => {
            window.removeEventListener('resize', updateMenuPosition);
            window.removeEventListener('scroll', updateMenuPosition, true);
        };
    }, [isOpen, menuPortal, updateMenuPosition, searchTerm, filteredOptions.length]);

    useEffect(() => {
        if (!isOpen || !menuPortal || !menuPosition) return undefined;
        const id = window.requestAnimationFrame(() => {
            const menu = menuRef.current;
            if (!menu) return;
            const search = menu.querySelector('.reusable-dropdown-search');
            if (search instanceof HTMLInputElement) {
                search.focus({ preventScroll: true });
            }
        });
        return () => window.cancelAnimationFrame(id);
    }, [isOpen, menuPortal, menuPosition]);

    const selectedItem = options.find(opt => opt.value === selectedOption);
    const selectedLabel =
        selectedItem?.label ||
        (typeof getSelectedLabel === 'function' ? getSelectedLabel(selectedOption) : '') ||
        selectedOption;
    const selectedIcon = selectedItem?.[optionIconKey];

    const menuEl = (
        <div
            ref={menuRef}
            className={[
                'reusable-dropdown-menu',
                menuPortal ? 'reusable-dropdown-menu--portal' : '',
                menuPortalClassName,
            ]
                .filter(Boolean)
                .join(' ')}
            style={menuPortal && menuPosition ? menuPosition : undefined}
            tabIndex={-1}
            role="listbox"
            onKeyDown={handleMenuKeyDown}
        >
            {searchable ? (
                <div className="reusable-dropdown-search-wrap">
                    <input
                        type="text"
                        className="reusable-dropdown-search"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={searchPlaceholder}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={handleSearchKeyDown}
                    />
                </div>
            ) : null}
            {filteredOptions.map((option, index) => (
                <div
                    key={index}
                    className={[
                        'reusable-dropdown-menu-item',
                        option.disabled ? 'reusable-dropdown-menu-item--disabled' : '',
                    ]
                        .filter(Boolean)
                        .join(' ')}
                    onClick={() => {
                        if (option.disabled) return;
                        handleOptionClick(option);
                    }}
                    aria-disabled={option.disabled ? true : undefined}
                >
                    <span className="reusable-dropdown-option-row">
                        {renderOptionIcon
                            ? renderOptionIcon(option)
                            : option?.[optionIconKey]
                              ? <span className="reusable-dropdown-option-icon">{option[optionIconKey]}</span>
                              : null}
                        <span>{option.label}</span>
                    </span>
                </div>
            ))}
            {filteredOptions.length === 0 ? <div className="reusable-dropdown-empty">No results</div> : null}
        </div>
    );

    return (
        <div className={`reusable-dropdown ${isOpen ? 'open' : ''}`} ref={dropdownRef}>
            <div className="reusable-dropdown-text" ref={triggerRef} onClick={toggleDropdown}>
                <span className="reusable-dropdown-selected">
                    {renderOptionIcon ? renderOptionIcon(selectedItem) : selectedIcon ? <span className="reusable-dropdown-option-icon">{selectedIcon}</span> : null}
                    <span>{selectedLabel}</span>
                </span>
                <MdOutlineExpandMore className="reusable-dropdown-chevron" aria-hidden />
            </div>
            {isOpen && !menuPortal ? menuEl : null}
            {isOpen && menuPortal && menuPosition ? createPortal(menuEl, document.body) : null}
        </div>
    );
};

export default Dropdown;